import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, unwrap } from "@/lib/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const r = await api.get("/auth/me");
      setUser(unwrap(r));
    } catch {
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    const data = unwrap(r) || {};
    if (!data.access_token) throw new Error("No access token returned");
    setToken(data.access_token);
    // Resolve the authoritative profile. Don't depend on the exact shape of the
    // /auth/login payload — if `user` is missing (older/stale backend), fetch it
    // from /auth/me so the post-login redirect always has a user to gate on.
    let u = data.user;
    if (!u) {
      try {
        const me = await api.get("/auth/me");
        u = unwrap(me);
      } catch {
        u = null;
      }
    }
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
